/**
 * iter579 · Global "Download on the App Store" surfaces
 *
 * Exports two pieces:
 *   • <AppStoreBadge/>          — reusable inline pill (liquid glass, dark).
 *                                 Drop it wherever a "Get the app" CTA
 *                                 makes sense (home hero, network profile,
 *                                 stories index footer, etc.).
 *   • <AppStorePromoPopup/>     — floating card that fades in 5 seconds
 *                                 after the first paint of any web
 *                                 session. Dismissible; remembers dismiss
 *                                 in sessionStorage so it never nags
 *                                 twice in the same tab.
 *
 * Both surfaces are HIDDEN inside the native iOS Capacitor shell — an
 * "install the app" prompt inside the app itself is exactly what Apple
 * flags on review, and also plainly redundant.
 */
import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { trackAppStoreClick } from '../lib/metaTracking';

export const APP_STORE_URL = 'https://apps.apple.com/us/app/flyt-house/id6785699524';

// Detect native iOS Capacitor at render time — same pattern used elsewhere
// (`checkoutRetry.js`, `Footer.js`) so behaviour stays consistent.
const isNativeIOS = () => {
  if (typeof window === 'undefined') return false;
  const cap = window.Capacitor;
  return !!cap?.isNativePlatform?.() && cap?.getPlatform?.() === 'ios';
};

// Apple's official Apple-logo SVG glyph (mono, inherits currentColor).
const AppleGlyph = ({ className = 'w-4 h-4' }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
    <path d="M17.05 12.87c-.02-2.21 1.8-3.27 1.88-3.32-1.03-1.5-2.62-1.7-3.19-1.73-1.36-.14-2.65.8-3.35.8-.7 0-1.76-.78-2.9-.76-1.49.02-2.87.87-3.64 2.2-1.55 2.69-.4 6.66 1.11 8.84.74 1.06 1.62 2.25 2.77 2.21 1.11-.05 1.53-.72 2.87-.72 1.34 0 1.72.72 2.9.7 1.2-.02 1.96-1.08 2.69-2.15.85-1.24 1.2-2.44 1.22-2.5-.03-.01-2.34-.9-2.36-3.57zM14.9 6.35c.6-.73 1.01-1.75.9-2.76-.87.04-1.93.58-2.55 1.3-.56.64-1.05 1.68-.92 2.67.97.08 1.97-.5 2.57-1.21z" />
  </svg>
);

/**
 * Reusable Apple App Store pill. Two visual variants:
 *   • variant="dark" (default) — black glass, white foreground; suits
 *     hero sections and dark cards.
 *   • variant="light"           — white pill, black foreground; suits
 *     bright/neutral surfaces (e.g. stories index).
 */
export const AppStoreBadge = ({ variant = 'dark', className = '', size = 'md', testid = 'app-store-badge' }) => {
  if (isNativeIOS()) return null;
  const base = variant === 'light'
    ? 'bg-white text-black ring-1 ring-black/10 hover:bg-neutral-100'
    : 'bg-black text-white ring-1 ring-white/10 hover:bg-neutral-900';
  const pad = size === 'lg'
    ? 'px-5 py-3'
    : size === 'sm'
    ? 'px-3 py-1.5'
    : 'px-4 py-2.5';
  const iconSize = size === 'lg' ? 'w-5 h-5' : size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';
  const eyebrowSize = size === 'lg' ? 'text-[10px]' : 'text-[9px]';
  const labelSize = size === 'lg' ? 'text-base' : size === 'sm' ? 'text-xs' : 'text-sm';
  return (
    <a
      href={APP_STORE_URL}
      target="_blank"
      rel="noreferrer"
      onClick={() => trackAppStoreClick('badge')}
      className={`inline-flex items-center gap-2.5 rounded-2xl font-bold transition ${base} ${pad} ${className}`}
      data-testid={testid}
    >
      <AppleGlyph className={iconSize} />
      <span className="flex flex-col items-start leading-none">
        <span className={`${eyebrowSize} uppercase tracking-widest opacity-70`}>Download on the</span>
        <span className={`${labelSize} font-black`}>App Store</span>
      </span>
    </a>
  );
};

const SESSION_FLAG = 'flyt.appstore.popup.dismissed.v1';

export const AppStorePromoPopup = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Hide inside the native shell + on any route we don't want to
    // interrupt (admin console, staff dashboard). Everywhere else is
    // fair game. sessionStorage keeps it once-per-tab.
    if (isNativeIOS()) return;
    try {
      if (window.sessionStorage.getItem(SESSION_FLAG) === '1') return;
    } catch { /* private mode — still show, just don't remember */ }
    const path = window.location.pathname || '';
    if (path.startsWith('/Penelopesims')
        || path.startsWith('/staff')
        || path.startsWith('/school')
        || path.startsWith('/athlete/invite')) return;
    const t = setTimeout(() => setVisible(true), 5000);
    return () => clearTimeout(t);
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    try { window.sessionStorage.setItem(SESSION_FLAG, '1'); } catch { /* fine */ }
    setVisible(false);
  };

  return (
    <div
      className="fixed z-[9997] bottom-5 left-1/2 -translate-x-1/2 w-[calc(100vw-3rem)] max-w-[19rem] sm:left-auto sm:right-4 sm:translate-x-0 sm:bottom-6 sm:w-auto sm:max-w-[22rem] animate-in slide-in-from-bottom-4 fade-in duration-500"
      role="dialog"
      aria-label="Download Flythouse on the App Store"
      data-testid="app-store-popup"
    >
      <div className="relative overflow-hidden rounded-[32px] liquid-glass-bottom-nav backdrop-blur-[28px] backdrop-saturate-150 ring-1 ring-white/15 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.75)]">
        {/* Specular sheen along the top edge — matches the nav's liquid glass */}
        <div className="pointer-events-none absolute inset-x-6 top-0 h-px bg-white/30" aria-hidden />

        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md text-white flex items-center justify-center transition ring-1 ring-white/15"
          data-testid="app-store-popup-dismiss"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="relative p-4 pr-12">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-11 h-11 rounded-2xl bg-white/10 ring-1 ring-white/15 flex items-center justify-center">
              <AppleGlyph className="w-6 h-6 text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.28em] text-white/60 font-black">Flythouse</p>
              <p className="text-white font-black leading-tight text-base">The app lands smoother.</p>
            </div>
          </div>
          <p className="text-[13px] text-white/70 leading-relaxed mb-4">
            Live Activities, push alerts the second your athlete's game moves, one-tap FLYT ID sharing. Free on iOS.
          </p>
          <div className="flex items-center gap-2">
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noreferrer"
              onClick={() => { trackAppStoreClick('promo_popup'); dismiss(); }}
              className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-2xl bg-white text-black font-black text-sm hover:bg-neutral-100 transition"
              data-testid="app-store-popup-cta"
            >
              <AppleGlyph className="w-4 h-4" />
              Download on the App Store
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppStoreBadge;
